// Original: https://github.com/chenglou/react-motion/tree/master/demos/demo8-draggable-list

import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import { clamp } from 'lodash';
import swap from 'lodash-move';
import { useDrag } from '@use-gesture/react';
import { useSprings, animated, to } from 'react-spring';

// Returns fitting styles for dragged/idle items
const fn = (order, down, originalIndex, curIndex, y) => index => (down && index === originalIndex
  ? {
    y: curIndex * 70 + y, scale: 1.1, zIndex: '1', shadow: 15, immediate: n => n === 'y' || n === 'zIndex',
  }
  : {
    y: order.indexOf(index) * 70, scale: 1, zIndex: '0', shadow: 1, immediate: false,
  });

const DraggableList = ({
  items, Component, itemName, handleClick, handleDragEnd, classes, postCategory,
}) => {
  const order = useRef(items.map((_, index) => index)); // Store indicies as a local ref, this represents the item order
  const [springs, springApi] = useSprings(items.length, fn(order.current)); // Create springs, each corresponds to an item, controlling its transform, scale, etc.
  const bind = useDrag(({ args: [originalIndex], down, movement: [, y] }) => {
    const curIndex = order.current.indexOf(originalIndex);
    const curRow = clamp(Math.round((curIndex * 70 + y) / 70), 0, items.length - 1);
    const newOrder = swap(order.current, curIndex, curRow);
    springApi.start(fn(newOrder, down, originalIndex, curIndex, y)); // Feed springs new style data, they'll animate the view without causing a single render
    if (!down) {
      order.current = newOrder;
      if (handleDragEnd) handleDragEnd(newOrder);
    }
  });

  return (
    <div className={`draggable-content relative ${classes}`} style={{ height: items.length * 70 }}>
      {springs.map(({
        zIndex, shadow, y, scale,
      }, i) => (
        <animated.div
          {...bind(i)}
          key={i}
          style={{
            zIndex,
            // @use-gesture 10 needs this or the browser scrolls instead of dragging.
            touchAction: 'none',
            boxShadow: shadow.to(s => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
            transform: to([y, scale], (y, s) => `translate3d(0,${y}px,0)`),
          }}
        >
          <Component item={items[i]} itemName={itemName} handleClick={handleClick} postCategory={postCategory} />
        </animated.div>
      ))}
    </div>
  );
};

DraggableList.propTypes = {
  handleDragEnd: PropTypes.func,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  Component: PropTypes.func.isRequired,
  itemName: PropTypes.string,
  handleClick: PropTypes.func,
  classes: PropTypes.string,
};

DraggableList.defaultProps = {
  handleDragEnd: null,
  items: [],
  itemName: '',
  handleClick: null,
  classes: '',
};

export default DraggableList;
